import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, DatePicker, Input, type InputRef } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { SpecialHours, TimeRange } from '@/apis/types';
import { CustomButton } from '@/components';
import TimeRangeList from './TimeRangeList';
import {
  DEFAULT_RANGE,
  MAX_SPECIAL_SPAN_DAYS,
  daysBetween,
  rangesError,
  sortRanges,
} from './operatingHours';
import styles from './OperatingHours.module.scss';

interface SpecialHoursEditorProps {
  entry: SpecialHours;
  isNew: boolean;
  onSave: (entry: SpecialHours) => void;
  onCancel: () => void;
}

const DATE_FORMAT = 'MM/DD/YYYY';
const toDayjs = (value: string | null) => (value ? dayjs(value) : null);
const fromDayjs = (value: Dayjs | null) =>
  value ? value.format('YYYY-MM-DD') : null;

/** Inline "Edit special hours" form shown in place of a holiday card. */
const SpecialHoursEditor: React.FC<SpecialHoursEditorProps> = ({
  entry,
  isNew,
  onSave,
  onCancel,
}) => {
  const [draft, setDraft] = useState<SpecialHours>(entry);
  const [submitted, setSubmitted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<InputRef>(null);

  // Opened from below the fold ("Add date", a pencil): bring it into view
  // clear of the drawer's sticky Save bar, and move focus into it because
  // the button that opened it is gone.
  useEffect(() => {
    containerRef.current?.scrollIntoView?.({
      block: 'center',
      behavior: 'smooth',
    });
    nameRef.current?.focus({ preventScroll: true });
  }, []);
  const set = (patch: Partial<SpecialHours>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const errors = {
    name: draft.name.trim() ? null : 'Enter a name',
    date: draft.start_date ? null : 'Pick a date',
    until: !(draft.end_date && draft.start_date)
      ? null
      : draft.end_date < draft.start_date
        ? 'Must be on or after the date'
        : daysBetween(draft.start_date, draft.end_date) >= MAX_SPECIAL_SPAN_DAYS
          ? `Can cover at most ${MAX_SPECIAL_SPAN_DAYS} days`
          : null,
    hours:
      !draft.closed && draft.hours.length === 0
        ? 'Add at least one time range'
        : !draft.closed
          ? rangesError(draft.hours)
          : null,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const handleSave = () => {
    setSubmitted(true);
    if (hasErrors) return;
    onSave({
      ...draft,
      name: draft.name.trim(),
      end_date:
        draft.end_date && draft.end_date !== draft.start_date
          ? draft.end_date
          : null,
      hours: draft.closed ? [] : sortRanges(draft.hours),
    });
  };

  const showError = (message: string | null, id: string) =>
    submitted && message ? (
      <div className={styles.fieldError} id={id} role='alert'>
        {message}
      </div>
    ) : null;
  // aria-describedby/aria-invalid for a field with an error showing.
  const errorProps = (message: string | null, id: string) =>
    submitted && message
      ? { 'aria-describedby': id, 'aria-invalid': true as const }
      : {};

  return (
    <div
      ref={containerRef}
      className={styles.editor}
      role='group'
      aria-label={isNew ? 'Add special hours' : 'Edit special hours'}
    >
      <div className={styles.editorTitle}>
        {isNew ? 'Add special hours' : 'Edit special hours'}
      </div>

      <div className={styles.editorField}>
        <label className={styles.editorLabel} htmlFor='special-hours-name'>
          Name
        </label>
        <Input
          id='special-hours-name'
          ref={nameRef}
          variant='filled'
          className={styles.filledControl}
          value={draft.name}
          maxLength={100}
          placeholder='e.g. Thanksgiving'
          onChange={(e) => set({ name: e.target.value })}
          {...errorProps(errors.name, 'special-hours-name-error')}
        />
        {showError(errors.name, 'special-hours-name-error')}
      </div>

      <div className={`${styles.editorField} ${styles.dateRow}`}>
        <div>
          <label className={styles.editorLabel} htmlFor='special-hours-date'>
            Date
          </label>
          <DatePicker
            id='special-hours-date'
            variant='filled'
            className={styles.filledControl}
            format={DATE_FORMAT}
            placeholder='Enter date'
            value={toDayjs(draft.start_date)}
            onChange={(value) => set({ start_date: fromDayjs(value) ?? '' })}
            allowClear={false}
            {...errorProps(errors.date, 'special-hours-date-error')}
          />
          {showError(errors.date, 'special-hours-date-error')}
        </div>
        <div>
          <label className={styles.editorLabel} htmlFor='special-hours-until'>
            Until (optional)
          </label>
          <DatePicker
            id='special-hours-until'
            variant='filled'
            className={styles.filledControl}
            format={DATE_FORMAT}
            placeholder='Enter date'
            value={toDayjs(draft.end_date)}
            disabledDate={(current) =>
              !!draft.start_date &&
              current.isBefore(dayjs(draft.start_date), 'day')
            }
            onChange={(value) => set({ end_date: fromDayjs(value) })}
            {...errorProps(errors.until, 'special-hours-until-error')}
          />
          {showError(errors.until, 'special-hours-until-error')}
        </div>
      </div>

      <div className={styles.editorField}>
        <span className={styles.editorLabel} id='special-hours-mode'>
          Hours on these dates
        </span>
        <div
          className={styles.hoursToggle}
          role='group'
          aria-labelledby='special-hours-mode'
        >
          {[
            { closed: true, text: 'Closed' },
            { closed: false, text: 'Special hours' },
          ].map((option) => (
            <button
              key={option.text}
              type='button'
              aria-pressed={draft.closed === option.closed}
              className={`${styles.hoursToggleOption} ${
                draft.closed === option.closed ? styles.hoursToggleActive : ''
              }`}
              onClick={() =>
                set({
                  closed: option.closed,
                  hours:
                    !option.closed && draft.hours.length === 0
                      ? [{ ...DEFAULT_RANGE }]
                      : draft.hours,
                })
              }
            >
              {option.text}
            </button>
          ))}
        </div>
      </div>

      {!draft.closed && (
        <div className={styles.editorField}>
          <TimeRangeList
            ranges={draft.hours}
            onChange={(hours: TimeRange[]) => set({ hours })}
            label={draft.name.trim() || 'Special hours'}
          />
          {submitted &&
            draft.hours.length === 0 &&
            showError(errors.hours, 'special-hours-hours-error')}
        </div>
      )}

      <Checkbox
        checked={draft.repeats_yearly}
        onChange={(e) => set({ repeats_yearly: e.target.checked })}
      >
        Repeat every year
      </Checkbox>

      {/* Same size and style as the Add contact modal's Cancel/Save.
          Delete lives on the card's trash icon, as elsewhere in Settings. */}
      <div className={styles.editorButtons}>
        <CustomButton
          text='Cancel'
          bgColor='transparent'
          color='var(--text-primary)'
          fontSize='14px'
          fontWeight='600'
          boxShadow='none'
          border='none'
          height='40px'
          onClick={onCancel}
        />
        <CustomButton
          text='Save'
          bgColor='#000'
          color='#fff'
          height='40px'
          width='auto'
          fontSize='14px'
          fontWeight='600'
          boxShadow='none'
          borderRadius='8px'
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default SpecialHoursEditor;
